import * as THREE from "three";
import { I8thWallImageTargetEventModel, IPipelineModule } from ".";
import mitt, { Emitter } from "mitt";
import { ACESFilmicToneMapping, EquirectangularReflectionMapping } from "three";
import EmptyWarehouseHDR from "/empty_warehouse_01_1k.hdr";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";
import { EJPlayer, EJPlayerBase, EJWebGLRenderer } from "../libs/ejx.es";
import { RendererEvents } from "..";
import type { ContentState, UIContentState } from "../image-targets";
window.THREE = THREE;

export type ImageTargetEvents = {
  "on-show-target": {
    detail: I8thWallImageTargetEventModel["detail"];
  };
  "on-update-target": {
    detail: I8thWallImageTargetEventModel["detail"];
  };
  "on-update-scene": void;
  "on-hide-target": {
    detail: I8thWallImageTargetEventModel["detail"];
  };
  "on-targets-scanned": {
    detail: I8thWallImageTargetEventModel["detail"];
  };
  "on-render": void;
  "content-load-progress": number;
  "content-loaded": void;
  setup: {
    player: EJPlayerBase;
  };
  "resume-tracking": void;
  "pause-tracking": void;
  "start-transition": {
    contentState: ContentState;
    animationState: "in" | "out";
  };
  "set-ui-state": UIContentState;
};

export type ImageTargetPipelineModuleResult = IPipelineModule & {
  emitter: Emitter<Partial<RendererEvents>>;
};

export const imageTargetPipelineModule = (): any => {
  let player: EJPlayerBase | undefined;
  const emitter = mitt<ImageTargetEvents>();
  let allowCanvasResize = true;
  let updatedCanvasWidth = 0;
  let updatedCanvasHeight = 0;

  const targetFound = ({ detail }: I8thWallImageTargetEventModel) => {
    emitter.emit("on-show-target", { detail });
  };

  const updateTarget = ({ detail }: I8thWallImageTargetEventModel) => {
    emitter.emit("on-update-target", { detail });
  };

  const hideTarget = ({ detail }: I8thWallImageTargetEventModel) => {
    emitter.emit("on-hide-target", { detail });
  };

  const imageLoading = ({ detail }: I8thWallImageTargetEventModel) => {
    console.log("image targets: ", detail);
  };

  return {
    name: "image-target",
    emitter,
    onStart: ({ canvas, GLctx }) => {
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(
        75,
        window.innerWidth / window.innerHeight,
        0.1,
        1000
      );

      camera.position.set(0, 1.5, 0);
      scene.add(camera);
      const renderer = new EJWebGLRenderer({
        canvas,
        context: GLctx!,
      });
      renderer.autoClear = false;

      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setClearColor(0x000000, 0);

      renderer.physicallyCorrectLights = true;
      renderer.toneMapping = ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1;

      new RGBELoader().load(EmptyWarehouseHDR, function (texture) {
        texture.mapping = EquirectangularReflectionMapping;
        scene.environment = texture;
      });

      player = new EJPlayer({
        canvas,
        // camera,
        renderer,
        scene,
      });
      if (player) {
        if (player.controls) {
          player.controls.dispose();
          player.controls = null;
        }
        if (player.orbitControls) {
          player.orbitControls.dispose();
          player.orbitControls = null;
        }
        player.renderer.setSize(window.innerWidth, window.innerHeight);

        player.resize();

        emitter.emit("setup", { player });
      }

      // prevent scroll/pinch gestures on canvas
      canvas?.addEventListener("touchmove", (event) => {
        event.preventDefault();
      });

      // Sync the xr controller's 6DoF position and camera paremeters with our scene.
      // @ts-expect-error: error for XR8 on window
      XR8.XrController.updateCameraProjectionMatrix({
        origin: camera.position,
        facing: camera.quaternion,
      });
    },
    onUpdate: ({ processCpuResult }) => {
      emitter.emit("on-update-scene");
      const realitySource =
        processCpuResult.reality || processCpuResult.facecontroller;

      if (!realitySource) {
        return;
      }

      const { rotation, position, intrinsics } = realitySource;
      const { camera } = player;

      for (let i = 0; i < 16; i++) {
        camera.projectionMatrix.elements[i] = intrinsics[i];
      }

      // Fix for broken raycasting in r103 and higher. Related to:
      //   https://github.com/mrdoob/three.js/pull/15996
      // Note: camera.projectionMatrixInverse wasn't introduced until r96 so check before setting
      // the inverse
      if (camera.projectionMatrixInverse) {
        camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();
      }

      if (rotation) {
        camera.setRotationFromQuaternion(rotation as THREE.Quaternion);
      }
      if (position) {
        camera.position.set(position.x, position.y, position.z);
      }
      camera.updateMatrix();
      camera.updateMatrixWorld();
    },
    onRender: () => {
      emitter.emit("on-render");
      if (!player) {
        console.warn(
          "ejPlayerPipelineModule: onRender but no `player` to update."
        );
        return;
      }
      player.render();
      player.renderer.clearDepth();
    },
    onException: (error: Error) => {
      console.log("Image target pipeline module error: ", error);
    },
    onCanvasSizeChange: ({ canvasWidth, canvasHeight }) => {
      if (!player) {
        console.warn(
          "ejPlayerPipelineModule: onCanvasSizeChange but no `player` to update."
        );
        return;
      }
      const devicePixelRatio = 1;
      const updateCameraAndRenderer = true;
      // const canvasWidth = window.innerWidth;
      // const canvasHeight = window.innerHeight;
      // temp hacky fix for the canvas resize breaking after an orientation change
      if (allowCanvasResize) {
        updatedCanvasWidth = canvasWidth;
        updatedCanvasHeight = canvasHeight;
      }
      player.renderer.setSize(canvasWidth, canvasHeight);
      player.resize(
        updatedCanvasWidth,
        updatedCanvasHeight,
        devicePixelRatio,
        allowCanvasResize ? updateCameraAndRenderer : false
      );
      emitter.emit("resized", {
        width: canvasWidth,
        height: canvasHeight,
        dpi: devicePixelRatio,
      });
      allowCanvasResize = true;
    },
    onDeviceOrientationChange: () => {
      allowCanvasResize = false;
    },

    listeners: [
      { event: "reality.imageloading", process: imageLoading },
      { event: "reality.imagefound", process: targetFound },
      { event: "reality.imageupdated", process: updateTarget },
      { event: "reality.imagelost", process: hideTarget },
    ],
  };
};
